import { NetworkAssessmentAnalysisType } from "@app/projects/enumerators";
import { RegionGranularity } from "@app/region-sets";
import { Component } from "react";
import { AdequacyAggregateGranularity, AdequacyAggregateType, ExportFormatType, ExportGapFilterType } from "../enumerations";
import { ExportAdequacyAggregatesModel, ExportAttestationAggregatesModel, ExportCompositeAggregatesModel, RegionSetFilter, SpecialtyFilter } from "../exportModels";
import ExportService from "../exportService";
import * as helpers from "./exportPanelBodyHelpers";
import { SelectFormat, SelectSpecialtyFilter } from "./filters";
import SelectStatusFilter from "./filters/SelectStatusFilter";
import GenerateBody from "./generate-body";
import { ReportPanelBodyBaseProps, ReportPanelBodyBaseState } from "./reportPanelBodyBase";
import * as toast from "./toast";

interface AllSpecialtiesPanelBodyProps extends ReportPanelBodyBaseProps {
  projectId: number;
  runConfigurationId: number;
  specialtyGroupCodes: string[];
}

interface AllSpecialtiesPanelBodyState extends ReportPanelBodyBaseState {
  regionSetFilter: RegionSetFilter;
  sharedUsers: number[];
  specialtyFilter: SpecialtyFilter;
}

export default class AllSpecialtiesPanelBody extends Component<AllSpecialtiesPanelBodyProps, AllSpecialtiesPanelBodyState> {
  private readonly exportService = new ExportService();
  private readonly testIdSuffix = "all-specialties";

  constructor(props: AllSpecialtiesPanelBodyProps) {
    super(props);

    this.state = {
      ...this.getInitialBodyState(),
    };
  }

  public componentWillUnmount() {
    toast.dismiss();
  }

  public render() {
    return (
      <>
        <div className="row">
          <div className="col-12 col-sm-3">
            <p className="lead mb0 font-weight-bold">Status Filter{<span>: Selected</span>}</p>
            <p>Please select the Status to include:</p>

            <div className="form-group row">
              <label className="col-12 col-form-label ptb0 text-left">
                Include
              </label>
              <SelectStatusFilter
                disabled={true}
                onNetworkAssessmentAnalysisTypeChange={this.handleNetworkAssessmentAnalysisTypeChange}
                regionSetFilter={this.state.regionSetFilter}
                testIdSuffix={this.testIdSuffix}
              />
            </div>
          </div>
          <div className="col-12 col-sm-4">
            <SelectSpecialtyFilter
              adequacyAnalysisType="county"
              disabled={this.props.disableSpecialtyGroupFilters}
              onSpecialtyGapFilterChange={this.handleSpecialtyGapFilterChange}
              onSpecialtyIncludeChange={this.handleSpecialtyIncludeChange}
              onExpandableSpecialtyIncludeChange={() => void false}
              showExpandableSpecialtyOption={false}
              specialtyFilter={this.state.specialtyFilter}
              testIdSuffix={this.testIdSuffix}
            />
          </div>
          <div className="col-12 col-sm-4">
            <SelectFormat outputFormat={this.state.outputFormat} onOutputFormatChange={this.handleOutputFormatChange} excelDisabled={false} exportType="all-specialtes" />
          </div>
        </div>
        <GenerateBody
          {...this.props}
          emailOnSuccess={this.state.emailOnSuccess}
          reportName={this.state.reportName}
          onGenerateClick={this.handleGenerateClick}
          onEmailOnSuccessChange={this.handleEmailOnSuccessChange}
          onReportNameChange={this.handleReportNameChange}
          isLoading={this.state.loadingReportSettingsJSONStrings && this.state.loadingReportSettingsJSONStrings.indexOf(helpers.stringifyStateReportSettings(this.state)) > -1}
          isValid={(this.state.regionSetFilter.includeActive || this.state.regionSetFilter.includePending || this.state.regionSetFilter.includeApproved) && (this.state.specialtyFilter.includeFacilitySpecialties || this.state.specialtyFilter.includePhysicianSpecialties)}
          showSharedUsers={this.props.match.params.module === "adequacy"}
          sharedUsers={this.state.sharedUsers}
          updateSelectedSharedUsers={this.updateSharedUsers}
          exportType="AdequacyAggregate"
          projectId={this.props.projectId}
          networkAssessmentAnalysisType={this.state.regionSetFilter.networkAssessmentAnalysisType}
          aggregateType={AdequacyAggregateType.SpecialtyGroup}
        />
      </>
    );
  }

  private getInitialBodyState = (): AllSpecialtiesPanelBodyState => {
    return {
      emailOnSuccess: true,
      loadingReportSettingsJSONStrings: [],
      outputFormat: ExportFormatType.Excel,
      regionSetFilter: {
        filterType: ExportGapFilterType.All,
        includeActive: true,
        includeApproved: true,
        includePending: true,
        networkAssessmentAnalysisType: this.props.initialNetworkAssessmentAnalysisType,
      },
      reportName: helpers.reportName(this.props.comparedRunConfigurationId !== null ? "Specialties Comparison" : "Specialties"),
      sharedUsers: [],
      specialtyFilter: {
        filterType: ExportGapFilterType.All,
        includeFacilitySpecialties: true,
        includePhysicianSpecialties: true,
      },
    } as AllSpecialtiesPanelBodyState;
  }

  private handleOutputFormatChange = (outputFormat: ExportFormatType) => {
    this.setState({ outputFormat });
  }

  private handleGenerateClick = async () => {
    const stateReportSettingsJSON = helpers.stringifyStateReportSettings(this.state);
    this.setState((prevState) => ({
      ...prevState,
      loadingReportSettingsJSONStrings: [
        ...prevState.loadingReportSettingsJSONStrings,
        stateReportSettingsJSON,
      ],
    }));

    switch (this.props.match.params.module) {
      case "adequacy":
        await this.generateExportAdequacyAggregateModel();
        break;
      case "attestation":
        await this.generateExportAttestationAggregateModel();
        break;
      case "composite":
        await this.generateExportCompositeAggregateModel();
        break;
      default:
        break;
    }

    toast.toastInfo(this.state.reportName);
  }

  // Adequacy
  private generateExportAdequacyAggregateModel = async () => {
    const aggregateModel: ExportAdequacyAggregatesModel = {
      aggregateGranularity: this.getAggregateGranularity(this.props.granularity),
      aggregateType: AdequacyAggregateType.SpecialtyGroup,
      comparedProjectRunConfigurationId: this.props.comparedRunConfigurationId,
      customProjectId: this.props.projectId,
      eMailOnSuccess: this.state.emailOnSuccess,
      exportName: this.state.reportName,
      formatType: this.state.outputFormat,
      includeActiveRegionSets: this.state.regionSetFilter.includeActive,
      includeApprovedRegionSets: this.state.regionSetFilter.includeApproved,
      includeFacilitySpecialties: this.state.specialtyFilter.includeFacilitySpecialties,
      includePendingRegionSets: this.state.regionSetFilter.includePending,
      includePhysicianSpecialties: this.state.specialtyFilter.includePhysicianSpecialties,
      networkAssessmentAnalysisTypes: [this.state.regionSetFilter.networkAssessmentAnalysisType],
      projectRunConfigurationId: this.props.runConfigurationId,
      projectURL: this.props.projectURL,
      regionSetGapFilter: this.state.regionSetFilter.filterType,
      regionSetIds: this.props.regionSetIds,
      runAsynchronous: true,
      sharedUsers: this.state.sharedUsers,
      specialtyGapFilter: this.state.specialtyFilter.filterType,
      specialtyGroupCodes: this.props.specialtyGroupCodes,
    };
    await this.exportService.postExportAdequacyAggregates(aggregateModel);
  }

  // Attestation
  private generateExportAttestationAggregateModel = async () => {
    const aggregateModel: ExportAttestationAggregatesModel = {
      customProjectId: this.props.projectId,
      eMailOnSuccess: this.state.emailOnSuccess,
      exportName: this.state.reportName,
      formatType: this.state.outputFormat,
      includeActiveRegionSets: this.state.regionSetFilter.includeActive,
      includeApprovedRegionSets: this.state.regionSetFilter.includeApproved,
      includeFacilitySpecialties: true,
      includePendingRegionSets: this.state.regionSetFilter.includePending,
      includePhysicianSpecialties: true,
      projectRunConfigurationId: this.props.runConfigurationId,
      projectURL: this.props.projectURL,
      regionSetGapFilter: this.state.regionSetFilter.filterType,
      regionSetIds: this.props.regionSetIds,
      runAsynchronous: true,
      specialtyGapFilter: ExportGapFilterType.All,
      specialtyGroupCodes: this.props.specialtyGroupCodes,
    };
    await this.exportService.postExportAttestationAggregates(aggregateModel);
  }

  // Composite
  private generateExportCompositeAggregateModel = async () => {
    const aggregateModel: ExportCompositeAggregatesModel = {
      customProjectId: this.props.projectId,
      eMailOnSuccess: this.state.emailOnSuccess,
      exportName: this.state.reportName,
      formatType: this.state.outputFormat,
      includeActiveRegionSets: this.state.regionSetFilter.includeActive,
      includeApprovedRegionSets: this.state.regionSetFilter.includeApproved,
      includeFacilitySpecialties: true,
      includePendingRegionSets: this.state.regionSetFilter.includePending,
      includePhysicianSpecialties: true,
      projectRunConfigurationId: this.props.runConfigurationId,
      projectURL: this.props.projectURL,
      regionSetGapFilter: this.state.regionSetFilter.filterType,
      regionSetIds: this.props.regionSetIds,
      runAsynchronous: true,
      specialtyGapFilter: ExportGapFilterType.All,
      specialtyGroupCodes: this.props.specialtyGroupCodes,
    };
    await this.exportService.postExportCompositeAggregates(aggregateModel);
  }

  private handleEmailOnSuccessChange = (emailOnSuccess: boolean) => {
    this.setState({ emailOnSuccess });
  }

  private handleReportNameChange = (reportName: string) => {
    this.setState({ reportName });
  }

  private updateSharedUsers = (sharedUsers: number[]) => {
    this.setState({ sharedUsers });
  }

  private handleNetworkAssessmentAnalysisTypeChange = (networkAssessmentAnalysisType: NetworkAssessmentAnalysisType) => {
    this.setState((prevState) => ({
      ...prevState,
      regionSetFilter: {
        ...prevState.regionSetFilter,
        networkAssessmentAnalysisType,
      },
      reportSettingsHasChanges: true
    }));
  }

  private handleSpecialtyIncludeChange = (selected: boolean, isPhysician: boolean) => {

    let physiciansSelected: boolean = this.state.specialtyFilter.includePhysicianSpecialties;
    let facilitiesSelected: boolean = this.state.specialtyFilter.includeFacilitySpecialties;

    if (isPhysician) {
      physiciansSelected = selected;
    } else {
      facilitiesSelected = selected;
    }

    this.setState((prevState) => ({
      ...prevState,
      reportSettingsHasChanges: true,
      specialtyFilter: {
        ...prevState.specialtyFilter,
        includeFacilitySpecialties: facilitiesSelected,
        includePhysicianSpecialties: physiciansSelected,
      },
    }));
  }

  private handleSpecialtyGapFilterChange = (filterType: ExportGapFilterType) => {
    this.setState((prevState) => ({
      ...prevState,
      reportSettingsHasChanges: true,
      specialtyFilter: {
        ...prevState.specialtyFilter,
        filterType,
      },
    }));
  }

  private getAggregateGranularity = (granularity: RegionGranularity) => {
    switch (granularity) {
      case RegionGranularity.County:
        if (this.props.regionSetIds.length > 0) { // if any counties are selected, report at county granularity otherwise report at state granularity
          return AdequacyAggregateGranularity.County;
        } else {
          return AdequacyAggregateGranularity.State;
        }
      case RegionGranularity.State:
        if (this.props.regionSetIds.length > 0) { // if any states are selected, report at state granularity otherwise report at service area granularity
          return AdequacyAggregateGranularity.State;
        } else {
          return AdequacyAggregateGranularity.ServiceArea;
        }
      default:
        throw new Error(`No translation for RegionGranularity: '${granularity}' to AdequacyAggregateGranularity`);
    }
  }
}
